import { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styles from './InfoBox.module.scss';
import Button from 'client/components/aria/Button/Button';
import PropTypes from 'prop-types';
import defaultAriaProps from 'client/utils/constants/ariaPropTypes';
import { useAssetContactDetails, getAssetId } from './useAssetContactDetails';
import { useLinkedFiles } from './useLinkedFiles';
import { useDispatch, useSelector } from 'react-redux';
import { setSelected, setSelectedOpenContactDetails, setSelectedRedactedAsset } from 'client/store/mapSlice/mapSlice';
import { setSupplementaryDataAssetError } from 'client/store/SupplimentaryDataSlice/supplementaryDataSlice';
import { useSupplementaryDataFetch } from 'client/utils/hooks/useSupplementaryData';
import backhandIcon from 'client/assets/imgs/backhand.svg';
import Loading from 'client/components/ui/Loading/Loading';
import {
    ENHANCED_MEASURES_VARIANT_INFO,
    getEnhancedMeasuresVariant
} from 'client/utils/constants/enhancedMeasures';
import LinkedFileAndGuidance from './LinkedFilesAndGuidance';

const backhand = <img
    src={backhandIcon}
    alt={''} width={18} height={18} />;

const variants = Object.keys(ENHANCED_MEASURES_VARIANT_INFO).reduce((acc, variant) => {
    const variantInfo = ENHANCED_MEASURES_VARIANT_INFO[variant];
    const icon = variantInfo.showBackhandIcon ? backhand : null;

    acc[variant] = {
        id: variant,
        titleKey: variantInfo.titleKey,
        defaultTextKey: variantInfo.defaultTextKey,
        headerIcon: icon,
        assetIcon: icon
    };

    return acc;
}, {});

export const getInfoBoxVariant = getEnhancedMeasuresVariant;

const InfoBox = ({ variant, action, asset, assets, assetOwner, ...props }) => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const openFeatures = useSelector(state => state.map?.selected || []);
    const openFeatureId = openFeatures[0]?.getId();
    const openHiddenFeatures = useSelector(state => state.map?.selectedRedactedAsset);
    const openHiddenFeatureId = openHiddenFeatures?.dataproviderassigneduniqueid;
    const isSingleMode = Boolean(asset);
    const assetsToRender = isSingleMode ? [asset] : assets;

    const {
        hasMultipleContactDetails,
        isPending,
        error
    } = useAssetContactDetails(assetsToRender);

    const {
        linkedFilesAndGuidances,
        hasMultipleGuidances,
        shouldShowContactButton
    } = useLinkedFiles(assetsToRender, variant);

    const fetchForAssetIds = useSupplementaryDataFetch();
    // Retry is scoped to just this box's asset(s) — group-level retries (e.g. EnhancedMeasuresList)
    // handle their own bulk retry, so this only applies in single-asset mode
    const retryContactDetails = useCallback(async () => {
        const assetIds = assetsToRender.map(getAssetId).filter(Boolean);
        if (!assetIds.length) {
            return;
        }
        dispatch(setSupplementaryDataAssetError({ assetIds, error: null }));
        await fetchForAssetIds(assetIds);
    }, [assetsToRender, dispatch, fetchForAssetIds]);

    const info = variants[variant];

    const handleClick = (enhancedMeasure, { openContactDetails = false } = {}) => {
        const feature = enhancedMeasure.feature;
        if (feature) {
            dispatch((openContactDetails ? setSelectedOpenContactDetails : setSelected)([feature]));
        } else {
            dispatch(setSelectedRedactedAsset({
                dataproviderassigneduniqueid: enhancedMeasure.dataproviderassigneduniqueid || enhancedMeasure.id,
                infoBoxVariant: variant
            }));
        }
    };
    const defaultAction = () => assetsToRender.length > 0 && handleClick(assetsToRender[0], { openContactDetails: true });

    if (!info) {
        return null;
    }

    const assetOwnerLabel = assetOwner || assetsToRender[0]?.nuarActor?.label || intl.formatMessage({ id: 'enhancedMeasures.info.assetOwner' });
    const defaultDescription = (
        <FormattedMessage
            id={info.defaultTextKey}
            values={{
                assetOwner: assetOwnerLabel,
                strong: (chunks) => <strong>{chunks}</strong>
            }}
        />
    );

    const renderAssetDetails = () => {
        if (error) {
            return (
                <div className={styles.error}>
                    <h5 className='govuk-heading-s govuk-!-margin-0'><FormattedMessage id='enhancedMeasures.info.error' /></h5>
                    <p className='govuk-body govuk-!-font-size-16 govuk-!-margin-0'><FormattedMessage id='enhancedMeasures.info.errorMessage' /></p>
                    {isSingleMode && (
                        <Button
                            variant='default'
                            data-testid={`${props['data-testid']}-retryButton`}
                            className={[styles.contactButton, 'govuk-!-margin-bottom-0 govuk-!-margin-top-2'].join(' ')}
                            onPress={retryContactDetails}>
                            <FormattedMessage id='enhancedMeasures.info.retryButton' />
                        </Button>
                    )}
                </div>
            );
        }

        if (isPending) {
            return (
                <Loading data-testid={`${props['data-testid']}-loading`} variant='compact' />
            );
        }

        const hasSupplementaryContent = linkedFilesAndGuidances.some(item =>
            (item?.linkedFiles?.length || 0) > 0 || (item?.guidances?.length || 0) > 0
        );
        const shouldRenderContactDetails = !hasSupplementaryContent;

        const contactButton = () => <Button
            variant='info'
            data-testid={`${assetOwnerLabel}-${variant}-contactDetailsButton`}
            className={styles.contactButton}
            onPress={action || defaultAction}>
            <FormattedMessage
                id='enhancedMeasures.info.contactDetails'
            />
        </Button>;

        if (shouldRenderContactDetails) {
            return <>
                <span className='govuk-body-s govuk-!-margin-bottom-2'>
                    {defaultDescription}
                </span>
                {hasMultipleContactDetails
                    ? <p>
                        <FormattedMessage
                            id='enhancedMeasures.info.multipleContactDetails'
                        />
                    </p>
                    : contactButton()}
            </>;
        }

        return (<>
            {(!isSingleMode && hasMultipleGuidances)
                ? <p><FormattedMessage id='enhancedMeasures.info.multipleGuidance' /></p>
                : <LinkedFileAndGuidance
                    linkedFilesAndGuidances={linkedFilesAndGuidances}
                    defaultDescription={defaultDescription}
                    contactButton={contactButton}
                    showContactButton={shouldShowContactButton}
                />
            }
        </>);
    };

    return <div className={styles.container} {...props}>
        <h4 className={`${styles.heading} govuk-heading-s`}>
            {info.headerIcon}
            <FormattedMessage id={info.titleKey} />
        </h4>
        <div className={styles.contentContainer}>
            <div className={styles.content}>
                {renderAssetDetails()}
            </div>
            {!isSingleMode && assets && assets.length > 0 &&
                <div className={styles.assetList}>
                    <h5><FormattedMessage id='enhancedMeasures.info.assets' /></h5>
                    <ul>
                        {
                            assets.map((enhancedMeasure) => {
                                const selected = (openFeatureId && openFeatureId === enhancedMeasure.feature?.getId()) || (openHiddenFeatureId && openHiddenFeatureId === enhancedMeasure.id);
                                const assetName = enhancedMeasure.assetName || intl.formatMessage({ id: 'enhancedMeasures.info.hiddenAsset' });
                                const icon = enhancedMeasure.icon || null;
                                return (
                                    <li key={enhancedMeasure.id} className={selected ? styles.selected : ''}>
                                        {selected && <div className={styles.highlight} />}
                                        <Button variant='link' className={styles.asset} onPress={() => handleClick(enhancedMeasure)}>
                                            {icon || <div className={styles.iconPlaceholder} />}
                                            <div className={styles.assetName}>
                                                <span>{assetName}</span>
                                                {info.assetIcon}
                                            </div>
                                        </Button>
                                    </li>
                                );
                            })
                        }
                    </ul>
                </div>
            }
        </div>
    </div>;
};

InfoBox.propTypes = {
    ...defaultAriaProps,
    asset: PropTypes.object,
    assets: PropTypes.array,
    assetOwner: PropTypes.string,
    variant: PropTypes.oneOf(Object.keys(ENHANCED_MEASURES_VARIANT_INFO))
};

InfoBox.defaultProps = {
    asset: null,
    assets: [],
    assetOwner: '',
    props: {}
};

export default InfoBox;
